import { useEffect, useRef } from "react";

/**
 * Automatically refreshes data at fixed interval + when the tab becomes visible
 * again (e.g. user reopens the PWA from the Home). Guarantees quasi-real-time
 * updates without manual pull-to-refresh.
 *
 * Usage:
 *   useAutoRefresh(load, 20000);  // poll every 20 s + refresh on focus
 */
export default function useAutoRefresh(loadFn, intervalMs = 20000) {
  const savedFn = useRef(loadFn);
  savedFn.current = loadFn;

  useEffect(() => {
    const tick = () => {
      try { savedFn.current?.(); } catch { /* ignore */ }
    };
    const id = setInterval(tick, intervalMs);
    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };
    const onFocus = () => tick();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [intervalMs]);
}
